import Header from '../components/Header'
import Footer from '../components/Footer'
import { Link } from 'react-router-dom'

const EmailMarketing = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-white">
          <div className="relative bg-gray-900">
            <div className="absolute inset-0">
              <img
                className="w-full h-full object-cover opacity-30"
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2850&q=80"
                alt="Email marketing"
              />
            </div>
            <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
                  Email Marketing That <span className="text-indigo-600">Converts</span>
                </h1>
                <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-100">
                  Drive engagement, nurture leads, and boost sales with strategic email campaigns that deliver 42x ROI.
                </p>
                <div className="mt-10">
                  <Link
                    to="/contact"
                    className="inline-block bg-indigo-600 py-3 px-8 border border-transparent rounded-md text-base font-medium text-white hover:bg-indigo-700"
                  >
                    Get Your Free Audit
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-indigo-50">
          <div className="max-w-7xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-8 text-center sm:grid-cols-3">
              {[
                { name: 'Average ROI', value: '42x' },
                { name: 'Higher Conversion', value: '6x' },
                { name: 'Preferred Channel', value: '72%' },
                { name: 'Open Rate Increase', value: '50-300%' },
                { name: 'More Purchases', value: '3x' },
                { name: 'Cost Effective', value: '$1:$44' },
              ].map((stat) => (
                <div key={stat.name} className="px-6 py-8 bg-white rounded-lg shadow">
                  <p className="text-lg font-medium text-gray-500">{stat.name}</p>
                  <p className="mt-2 text-3xl font-extrabold text-indigo-600">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">
                Our Services
              </h2>
              <p className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
                Comprehensive Email Marketing Solutions
              </p>
            </div>

            <div className="mt-16">
              <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
                {[
                  {
                    name: 'Email Strategy',
                    description: 'Data-driven approach to maximize your email performance',
                    features: ['Audience segmentation', 'Send frequency analysis', 'Content mapping', 'KPI framework']
                  },
                  {
                    name: 'Campaign Management',
                    description: 'End-to-end creation and execution of high-converting campaigns',
                    features: ['Promotional emails', 'Seasonal campaigns', 'Product launches', 'Event invitations']
                  },
                  {
                    name: 'Automation Flows',
                    description: 'Set-it-and-forget-it sequences that nurture leads 24/7',
                    features: ['Welcome series', 'Cart abandonment', 'Re-engagement', 'Customer onboarding']
                  },
                  {
                    name: 'Newsletter Creation',
                    description: 'Regular touchpoints that build relationships and authority',
                    features: ['Content strategy', 'Responsive design', 'Performance tracking', 'A/B testing']
                  },
                  {
                    name: 'List Growth',
                    description: 'Strategies to expand your subscriber base with quality leads',
                    features: ['Lead magnet creation', 'Opt-in optimization', 'Landing pages', 'Referral programs']
                  },
                  {
                    name: 'Analytics & Optimization',
                    description: 'Continuous improvement based on real performance data',
                    features: ['Open/click tracking', 'Conversion analysis', 'List hygiene', 'Deliverability audits']
                  }
                ].map((service) => (
                  <div key={service.name} className="relative bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      {service.name}
                      <span className="absolute inset-0" aria-hidden="true" />
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      {service.description}
                    </p>
                    <ul className="mt-4 space-y-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-start">
                          <svg className="flex-shrink-0 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="ml-2 text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Case Study/Testimonial Section */}
        <div className="bg-indigo-700 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-2 lg:gap-8">
              <div className="mb-8 lg:mb-0">
                <h2 className="text-3xl font-extrabold text-white">
                  <span className="block">Results That Speak</span>
                  <span className="block">For Themselves</span>
                </h2>
                <p className="mt-4 text-lg text-indigo-200">
                  See how we helped eCommerce brand LuxeLiving increase their email revenue by 217% in just 3 months.
                </p>
                <div className="mt-8">
                  <Link to="/case-studies" className="inline-flex items-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50">
                    View Case Study
                  </Link>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-12 w-12 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <blockquote>
                      <p className="text-lg text-gray-700">
                        "The email automation sequences transformed our customer onboarding process. We saw a 40% increase in repeat purchases from new customers within the first 60 days."
                      </p>
                      <footer className="mt-4">
                        <div className="flex items-center">
                          <div className="ml-4">
                            <div className="text-base font-medium text-gray-900">Sarah Chen</div>
                            <div className="text-base font-medium text-indigo-600">CMO, LuxeLiving</div>
                          </div>
                        </div>
                      </footer>
                    </blockquote>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div id="contact" className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Ready to Transform Your Email Marketing?
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Get expert guidance and start seeing better results from your email campaigns.
            </p>
            <div className="mt-8 flex justify-center">
              <div className="inline-flex rounded-md shadow">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Get Started Today
                </Link>
              </div>
              <div className="ml-3 inline-flex">
                <Link
                  to="/case-studies"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default EmailMarketing
